import React from "react";
import { useTranslation } from "@me8eon/translation";
import { PRODUCTS, fmt, scoreColor, sentimentColor } from "../poData";

const GAUGE_COLORS: Record<string, string> = {
    good: "#1aa055",
    warn: "#f0a93b",
    bad: "#ea1b0a",
};

const ScoreGauge: React.FC<{ score: number }> = ({ score }) => {
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - score / 100);
    const color = GAUGE_COLORS[scoreColor(score)];
    return (
        <svg className="po-gauge" width="52" height="52" viewBox="0 0 52 52">
            <circle cx="26" cy="26" r={radius} fill="none" stroke="#eee" strokeWidth="4" />
            <circle
                className="po-gauge__arc"
                cx="26"
                cy="26"
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                transform="rotate(-90 26 26)"
                style={{ ["--gauge-circ" as string]: `${circumference}` } as React.CSSProperties}
            />
            <text x="26" y="26" textAnchor="middle" dominantBaseline="central" className="po-gauge__text" fill={color}>
                {score}
            </text>
        </svg>
    );
};

const ProductsTab: React.FC = () => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.products.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    return (
        <div className="po-products">
            <div className="po-section-head">
                <div>
                    <h2>{tp("title")}</h2>
                    <p className="po-section-head__sub">{PRODUCTS.length} {tp("products")} · {tc("last")} 30 {tc("days")}</p>
                </div>
            </div>

            <div className="po-product-grid">
                {PRODUCTS.map((p) => (
                    <div className="po-card po-product-card" key={p.name}>
                        <div className="po-product-card__head">
                            <div>
                                <h3 className="po-product-card__name">{p.name}</h3>
                                <span className="po-product-card__domain">{p.domain.toUpperCase()}</span>
                            </div>
                            <ScoreGauge score={p.score} />
                        </div>

                        <div className="po-product-card__stats">
                            <div className="po-product-card__stat">
                                <span>{tp("queryVolume")}</span>
                                <strong>{fmt(p.queryVolume)}</strong>
                            </div>
                            <div className="po-product-card__stat">
                                <span>{tp("incidents")}</span>
                                <strong className={p.incidents > 25 ? "bad" : ""}>{p.incidents}</strong>
                            </div>
                            <div className="po-product-card__stat">
                                <span>{tp("sentiment")}</span>
                                <strong className={sentimentColor(p.sentiment)}>{p.sentiment}%</strong>
                            </div>
                        </div>

                        <div className="po-product-card__health">
                            <div className="po-product-card__health-row">
                                <span>{tp("healthScore")}</span>
                                <span>{p.healthScore}/100</span>
                            </div>
                            <div className="po-progress">
                                <div
                                    className={`po-progress__fill po-progress__fill--${scoreColor(p.healthScore)}`}
                                    style={{ width: `${p.healthScore}%` }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProductsTab;
